import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
  Badge,
  Chip,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useThemeContext } from './ThemeContext';
import { useUser } from '../../contexts/UserContext';

const getPageTitle = (pathname, getUserDisplayName) => {
  const routeMap = {
    '/home': getUserDisplayName ? `Welcome, ${getUserDisplayName()}` : 'Welcome',
    '/dashboard': 'Dashboard',
    '/agent-studio': 'Agent Studio',
    '/cloud-management': 'Cloud Management',
    '/marketplace': 'Apphub',
    '/app-preview': 'Apphub',
    '/apphub': 'Apphub',
    '/meeting-summarizer': 'Meeting Summarizer',
    '/hashcat': 'HashCat',
    '/paas': 'PaaS',
    '/callbot': 'Callbot',
    '/piowidget': 'PioWidget',
    '/settings': 'Settings',
    '/admin': 'Admin',
    '/discover-instances': 'Discover Instances',
    '/chat-agent': 'Chat Agent',
  };
  
  if (pathname.startsWith('/monitoring/')) {
    return 'Node Monitoring';
  }
  if (pathname.startsWith('/api-keys/')) {
    return 'API Keys';
  }
  if (pathname.startsWith('/rag-upload/')) {
    return 'RAG Upload';
  }
  if (pathname.startsWith('/agents/')) {
    return 'Manage Agent';
  }
  
  return routeMap[pathname] || '';
};

export default function HeaderBar({ isCollapsed = false, sidebarHidden = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { keycloak } = useKeycloak();
  const { mode, toggleTheme } = useThemeContext();
  const { user, getUserDisplayName, getPrimaryRole, onlyServiceRoleUser } = useUser();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const notificationsOpen = Boolean(notificationsAnchorEl);
  
  const drawerWidth = 240;
  const collapsedDrawerWidth = 80;
  const currentDrawerWidth = sidebarHidden ? 0 : (isCollapsed ? collapsedDrawerWidth : drawerWidth);
  const pageTitle = getPageTitle(location.pathname, getUserDisplayName);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    keycloak.logout();
    navigate('/');
  };

  const handleSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const displayName = getUserDisplayName() || user?.email || 'User';

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: 0,
        left: { xs: 0, sm: `${currentDrawerWidth}px` },
        width: { xs: '100%', sm: `calc(100% - ${currentDrawerWidth}px)` },
        display: { xs: 'none', sm: 'block' },
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'left'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: '56px', sm: '64px' },
          px: { xs: 1, sm: 3 },
          justifyContent: 'space-between',
        }}
      >
        {pageTitle && (
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
              fontWeight: 600,
              color: 'text.primary',
              flexGrow: 0,
              mr: 3,
            }}
          >
            {pageTitle}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <Box
            component={Link}
            to="/documentation"
            onClick={(e) => {
              e.preventDefault();
              window.open('/documentation', '_blank');
            }}
            sx={{ 
              textDecoration: 'none',
              cursor: 'pointer',
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
              transition: 'all 0.2s ease',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              '&:hover': {
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
              }
            }}
          >
            <FileText size={16} />
            <Typography 
              component="span"
              sx={{ 
                fontWeight: 400,
                fontSize: '0.875rem'
              }}
            >
              docs
            </Typography>
          </Box>

          <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
            <IconButton
              onClick={toggleTheme}
              size="small"
              sx={{
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                },
              }}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <IconButton
              onClick={handleNotificationsOpen}
              size="small"
              sx={{
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                },
              }}
            >
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Box sx={{ ml: { xs: 0.5, sm: 1 }, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Account menu">
              <IconButton
                onClick={handleProfileMenuOpen}
                size="small"
                sx={{
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    border: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  {getUserInitials()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {displayName}
          </Typography>
          {user?.email && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {user.email}
            </Typography>
          )}
          {getPrimaryRole && (
            <Chip 
              label={getPrimaryRole()} 
              size="small" 
              sx={{ 
                mt: 0.5, 
                height: 20, 
                fontSize: '0.65rem',
                fontWeight: 500,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText
              }} 
            />
          )}
        </Box>
        <Divider />
        {!onlyServiceRoleUser && (
          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={notificationsAnchorEl}
        open={notificationsOpen}
        onClose={handleNotificationsClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 320,
            maxWidth: 400,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No new notifications
          </Typography>
        </Box>
      </Menu>
    </AppBar>
  );
}

